import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';

function AdminPollResponses() {
  const [poll, setPoll] = useState(null);
  const [responses, setResponses] = useState([]);
  const { pollId } = useParams();

  useEffect(() => {
    loadResponses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId]);

  const loadResponses = async () => {
    try {
      const [pollRes, responsesRes] = await Promise.all([
        axios.get(`${API_URL}/api/polls/${pollId}/details`),
        axios.get(`${API_URL}/api/admin/polls/${pollId}/responses`)
      ]);
      setPoll(pollRes.data);
      setResponses(responsesRes.data.responses || []);
    } catch (error) {
      console.error('Failed to load responses:', error);
    }
  };

  if (!poll) {
    return <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
    </div>;
  }

  const yesCount = responses.filter(r => r.answer === 'yes').length;
  const noCount = responses.filter(r => r.answer === 'no').length;
  const total = responses.length;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Poll Info */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-testid="poll-details">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">{poll.question}</h2>
        
        {/* Summary */}
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div className="bg-gray-100 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-gray-800">{total}</p>
            <p className="text-sm text-gray-600">Total Responses</p>
          </div>
          <div className="bg-green-100 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{yesCount}</p>
            <p className="text-sm text-gray-600">Yes ({total > 0 ? Math.round(yesCount/total*100) : 0}%)</p>
          </div>
          <div className="bg-red-100 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{noCount}</p>
            <p className="text-sm text-gray-600">No ({total > 0 ? Math.round(noCount/total*100) : 0}%)</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-8 bg-gray-200 rounded-full overflow-hidden flex">
          <div 
            className="bg-green-500 h-full transition-all"
            style={{ width: `${total > 0 ? (yesCount/total*100) : 50}%` }}
          ></div>
          <div 
            className="bg-red-500 h-full transition-all"
            style={{ width: `${total > 0 ? (noCount/total*100) : 50}%` }}
          ></div>
        </div>
      </div>

      {/* Responses Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="w-full" data-testid="responses-table">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">User</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Answer</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Location</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {responses.map((response, index) => (
              <tr key={index} className="hover:bg-gray-50" data-testid={`response-row-${index}`}>
                <td className="px-6 py-4 text-sm text-gray-800">{response.username}</td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    response.answer === 'yes' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {response.answer.toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {response.state && response.state !== 'N/A' 
                    ? `${response.state}, ${response.country}` 
                    : response.country}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {new Date(response.createdAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {responses.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p>No responses yet for this poll</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPollResponses;
